import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import { useSound } from '../../utils/sound/SoundProvider';

const DEFAULT_COLORS = [
  // Reds
  '#ef4444', '#dc2626', '#f87171', '#be123c', '#b91c1c',
  // Oranges
  '#f97316', '#ea580c', '#fb923c', '#fbbf24', '#f59e0b',
  // Yellows
  '#fde047', '#facc15', '#eab308', '#fef08a', '#fef9c3',
  // Greens
  '#84cc16', '#22c55e', '#16a34a', '#10b981', '#4ade80', '#34d399', '#a3e635', '#65a30d',
  // Cyans/Teals
  '#06b6d4', '#0891b2', '#2dd4bf', '#14b8a6', '#67e8f9', '#5eead4',
  // Blues
  '#3b82f6', '#2563eb', '#1d4ed8', '#60a5fa', '#38bdf8', '#0ea5e9', '#1e40af', '#1e3a8a',
  // Purples/Violets
  '#8b5cf6', '#a21caf', '#a78bfa', '#c4b5fd', '#7c3aed', '#6d28d9', '#f5d0fe', '#e879f9',
  // Pinks/Magentas
  '#ec4899', '#db2777', '#f472b6', '#f9a8d4', '#e11d48', '#f43f5e',
  // Browns/Neutrals
  '#92400e', '#78350f', '#a16207', '#f5f5f4', '#d4d4d4', '#a3a3a3', '#737373', '#404040', '#171717',
  // Black/White
  '#000000', '#ffffff',
];

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians)),
  };
}

function describeArcPath(startAngle, endAngle, radius = 46, center = 50) {
  const start = polarToCartesian(center, center, radius, endAngle);
  const end = polarToCartesian(center, center, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    `M ${center} ${center}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function getSegmentLabelPosition(midAngle, radius = 28, center = 50) {
  return polarToCartesian(center, center, radius, midAngle);
}

function getRandomColor(excludeColor = null) {
  if (DEFAULT_COLORS.length === 0) return '#10b981';
  if (DEFAULT_COLORS.length === 1) return DEFAULT_COLORS[0];
  const available = DEFAULT_COLORS.filter((color) => color !== excludeColor);
  const source = available.length > 0 ? available : DEFAULT_COLORS;
  return source[Math.floor(Math.random() * source.length)];
}

function getColorFromSequence(index, offset = 0) {
  if (DEFAULT_COLORS.length === 0) return '#10b981';
  const normalizedIndex = ((Number(index) || 0) + (Number(offset) || 0)) % DEFAULT_COLORS.length;
  return DEFAULT_COLORS[(normalizedIndex + DEFAULT_COLORS.length) % DEFAULT_COLORS.length];
}

function normalizeHexColor(color) {
  const value = String(color || '').trim();
  if (!/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(value)) return null;
  if (value.length === 7) return value.toLowerCase();
  return `#${value.slice(1).split('').map((char) => `${char}${char}`).join('')}`.toLowerCase();
}

function hexToRgb(color) {
  const normalized = normalizeHexColor(color);
  if (!normalized) return null;
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

function getReadableTextColor(backgroundColor) {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#f8fafc';
  const luminance = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
  return luminance >= 160 ? '#0f172a' : '#f8fafc';
}

function normalizeAngle(angle) {
  const normalized = angle % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function angleIsWithinSegment(angle, startAngle, endAngle) {
  const normalizedAngle = normalizeAngle(angle);
  const normalizedStart = normalizeAngle(startAngle);
  const normalizedEnd = normalizeAngle(endAngle);

  if (Math.abs(endAngle - startAngle) >= 359.95) return true;
  if (normalizedStart <= normalizedEnd) {
    return normalizedAngle >= normalizedStart && normalizedAngle < normalizedEnd;
  }
  return normalizedAngle >= normalizedStart || normalizedAngle < normalizedEnd;
}

function easeOutSpin(progress) {
  return 1 - Math.pow(1 - progress, 4);
}

// Counts how many segment edges the pointer swept past while rotation moved
// from prevRotation to nextRotation (rotation only ever increases during a spin).
function countBoundaryCrossings(prevRotation, nextRotation, boundaryAngles) {
  if (!boundaryAngles || boundaryAngles.length === 0 || nextRotation <= prevRotation) return 0;
  // The pointer angle is (360 - rotation) mod 360, so it sweeps backwards through
  // each boundary once per 360 degrees of rotation; count those wraps exactly.
  const uPrev = -prevRotation;
  const uNext = -nextRotation;
  let total = 0;
  for (const boundary of boundaryAngles) {
    total += Math.floor((boundary - uNext) / 360) - Math.floor((boundary - uPrev) / 360);
  }
  return total;
}



function EditRow({
  draft, choice, setDrafts, isBusy, setExpandedChoiceId, saveChoice
}) {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveChoice(choice.id);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        value={draft.text}
        onChange={(event) => setDrafts((current) => ({
          ...current,
          [choice.id]: { ...draft, text: event.target.value },
        }))}
        onKeyDown={handleKeyDown}
        disabled={isBusy}
        placeholder="Choice name"
        className="min-w-0 flex-1 rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none ring-0 transition focus:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
      />
      <button
        type="button"
        onClick={() => setExpandedChoiceId(null)}
        className="shrink-0 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
      >
        Close
      </button>
    </div>
  );
}

const MAX_CHOICES = 20;
function ChoiceList({
  choices,
  drafts,
  setDrafts,
  isHost,
  isBusy,
  dispatchAction,
  expandedChoiceId,
  setExpandedChoiceId,
  canClear,
  onAddChoice,
  onToggleImportPanel,
  isImportPanelOpen,
  importText,
  setImportText,
  importMode,
  setImportMode,
  onImportChoices,
  isCollapsed,
  onToggleCollapsed,
}) {
  const saveChoice = async (choiceId) => {
    const draft = drafts[choiceId];
    if (!draft) return;
    const trimmedText = (draft.text || '').slice(0, 30);
    const action = {
      type: 'updateChoice',
      choiceId,
      color: draft.color,
      text: trimmedText,
      weight: Number(draft.weight),
    };
    await dispatchAction(action);
    setExpandedChoiceId && setExpandedChoiceId(null);
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="flex items-center gap-2 text-left text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          aria-expanded={!isCollapsed}
        >
          <span>Choices</span>
          <span className="text-base leading-none">{isCollapsed ? '+' : '-'}</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 dark:text-slate-500">{choices.length} total</span>
          {isHost && (
            <>
              <button
                type="button"
                title="Randomize order"
                onClick={() => dispatchAction({ type: 'randomizeChoices' })}
                disabled={!canClear}
                className="rounded-full border border-slate-400 bg-white p-2 text-xs font-semibold text-slate-900 shadow hover:bg-emerald-100 hover:border-emerald-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-300 dark:text-slate-900 dark:hover:bg-emerald-200 flex items-center justify-center transition-all"
                style={{ width: 38, height: 38 }}
              >
                {/* Bolder Dice SVG icon */}
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" fill="#fff" stroke="#222" strokeWidth="2" />
                  <circle cx="8" cy="8" r="1.5" fill="#222" />
                  <circle cx="16" cy="8" r="1.5" fill="#222" />
                  <circle cx="8" cy="16" r="1.5" fill="#222" />
                  <circle cx="16" cy="16" r="1.5" fill="#222" />
                  <circle cx="12" cy="12" r="1.5" fill="#222" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => dispatchAction({ type: 'clearChoices' })}
                disabled={!canClear}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>
      {!isCollapsed && (
      <div className="space-y-3">
        {choices.map((choice) => {
          const draft = drafts[choice.id] || { text: choice.text, color: choice.color, weight: choice.weight };
          const isExpanded = expandedChoiceId === choice.id;
          return (
            <div key={choice.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-900/60">
              {isExpanded ? (
                <EditRow draft={draft} choice={choice} setDrafts={setDrafts} isBusy={isBusy} setExpandedChoiceId={setExpandedChoiceId} saveChoice={saveChoice} />
              ) : (
                <button
                  type="button"
                  onClick={() => setExpandedChoiceId((current) => (current === choice.id ? null : choice.id))}
                  className="flex w-full items-center justify-between gap-3 text-left"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{choice.text}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="h-5 w-5 rounded-full border-2 shadow-sm"
                      style={{
                        backgroundColor: choice.color,
                        boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                        borderColor: (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
                          ? '#fff'
                          : '#000',
                      }}
                    />
                    <span className="rounded-full bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      x{choice.weight}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
                      Edit
                    </span>
                  </div>
                </button>
              )}
              {isExpanded && (
                <>
                  <div className="mt-3 grid grid-cols-[56px_72px_minmax(0,1fr)_minmax(0,1fr)] gap-2">
                    <input
                      type="color"
                      value={draft.color}
                      onChange={(event) => setDrafts((current) => ({
                        ...current,
                        [choice.id]: { ...draft, color: event.target.value },
                      }))}
                      disabled={isBusy}
                      title="Color"
                      className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-950"
                    />
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={draft.weight}
                      onChange={(event) => setDrafts((current) => ({
                        ...current,
                        [choice.id]: { ...draft, weight: event.target.value },
                      }))}
                      disabled={isBusy}
                      placeholder="Weight"
                      className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0 transition focus:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => saveChoice(choice.id)}
                      disabled={isBusy}
                      className="rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => dispatchAction({ type: 'removeChoice', choiceId: choice.id })}
                      disabled={isBusy}
                      className="rounded-full border border-rose-300 bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:bg-rose-700 dark:text-white dark:hover:bg-rose-800"
                    >
                      Del
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        <div className="mt-2 space-y-3">
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              disabled={isBusy || choices.length >= MAX_CHOICES}
              className="rounded-2xl bg-emerald-500 px-6 py-2 text-base font-bold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={onAddChoice}
            >
              Add Choice
            </button>
            <button
              type="button"
              disabled={isBusy}
              className="rounded-2xl border border-slate-300 bg-white px-6 py-2 text-base font-bold text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
              onClick={onToggleImportPanel}
            >
              Import Choices
            </button>
          </div>

          {isImportPanelOpen && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Paste one choice per line
              </label>
              <textarea
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder="Option 1
Option 2
Option 3"
                rows={8}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-800 dark:bg-slate-900/70">
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setImportMode('replace')}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${importMode === 'replace'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                    aria-pressed={importMode === 'replace'}
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMode('append')}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${importMode === 'append'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                    aria-pressed={importMode === 'append'}
                  >
                    Append
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onImportChoices}
                  disabled={isBusy || !String(importText || '').trim()}
                  className="rounded-2xl bg-slate-900 px-5 py-2 text-sm font-bold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                >
                  Import
                </button>
                <button
                  type="button"
                  onClick={onToggleImportPanel}
                  disabled={isBusy}
                  className="rounded-2xl border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default function WheelOfDestinyGame({
  roomId,
  isHost,
  gameState,
  gamePlayers = [],
  user = {},
  socket,
  onGameAction,
}) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const sound = useSound();
  const [choiceDrafts, setChoiceDrafts] = useState({});
  const [expandedChoiceId, setExpandedChoiceId] = useState(null);
  const [pendingAddedChoiceId, setPendingAddedChoiceId] = useState(null);
  const [isChoicesCollapsed, setIsChoicesCollapsed] = useState(false);
  const [isTotalsCollapsed, setIsTotalsCollapsed] = useState(false);
  const [isImportPanelOpen, setIsImportPanelOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importMode, setImportMode] = useState('replace');
  const [rotation, setRotation] = useState(gameState?.currentRotation || 0);
  const animatedSpinRef = useRef(null);
  const animationFrameRef = useRef(null);
  const rotationRef = useRef(gameState?.currentRotation || 0);
  const suppressDefaultChoiceRef = useRef(false);
  const segmentBoundariesRef = useRef([]);

  const phase = gameState?.phase || 'collecting';
  const choices = useMemo(() => {
    if (gameState?.choices && gameState.choices.length > 0) return gameState.choices;
    // Always at least one choice (fallback)
    return [{
      id: 'default-choice-1',
      text: 'Choice 1',
      color: getRandomColor(),
      weight: 1,
    }];
  }, [gameState?.choices]);

  // If fallback default is shown, auto-add it as a real choice so it is editable
  useEffect(() => {
    if (suppressDefaultChoiceRef.current) {
      return;
    }
    if (
      gameState?.choices?.length === 0 &&
      choices.length === 1 && choices[0].id === 'default-choice-1'
    ) {
      // Add real default choice
      submitAction({
        type: 'addChoice',
        text: 'Choice 1',
        color: choices[0].color,
        weight: 1,
      });
    }
  }, [gameState?.choices, choices, submitAction]);
  const players = useMemo(() => gameState?.players || [], [gameState?.players]);
  const segments = useMemo(() => {
    if (gameState?.segments && gameState.segments.length > 0) return gameState.segments;
    // If using fallback default choice, synthesize a segment for it
    if (choices.length === 1 && choices[0].id === 'default-choice-1') {
      return [{
        id: 'default-choice-1',
        startAngle: 0,
        endAngle: 360,
        midAngle: 180,
        sweepAngle: 360,
      }];
    }
    return [];
  }, [gameState?.segments, choices]);
  useEffect(() => {
    segmentBoundariesRef.current = segments.map((segment) => segment.startAngle);
  }, [segments]);
  const selectionTotals = useMemo(() => Object.values(gameState?.selectionTotals || {}), [gameState?.selectionTotals]);
  const lockedInPlayers = gameState?.lockedInPlayers || {};
  const lastWinner = gameState?.lastWinner || null;
  const spin = gameState?.spin || null;
  const isBusy = phase === 'spinning';
  const isCurrentPlayerLocked = !!lockedInPlayers[user?.socketId];
  const everyoneLocked = players.length > 0 && players.every((player) => !!lockedInPlayers[player.socketId]);
  const orderedSelectionTotals = useMemo(
    () => [...selectionTotals].sort((left, right) => (right.count - left.count) || left.text.localeCompare(right.text)),
    [selectionTotals],
  );
  const maxSelectionCount = useMemo(
    () => orderedSelectionTotals.reduce((maxCount, entry) => Math.max(maxCount, entry.count || 0), 0),
    [orderedSelectionTotals],
  );
  const pointedChoice = useMemo(() => {
    if (choices.length === 0 || segments.length === 0) return null;
    const pointerAngle = normalizeAngle(360 - rotation);
    const activeSegment = segments.find((segment) => angleIsWithinSegment(pointerAngle, segment.startAngle, segment.endAngle));
    if (!activeSegment) return null;
    return choices.find((choice) => choice.id === activeSegment.id) || null;
  }, [choices, rotation, segments]);
  // Only show displayedChoice if spinning or there is a lastWinner (i.e., after first spin)
  let displayedChoice = null;
  if (phase === 'spinning') {
    displayedChoice = pointedChoice;
  } else if (lastWinner) {
    displayedChoice = lastWinner;
  }
  const centerFillColor = pointedChoice?.color || '#0f172a';


  // Wrap dispatchAction to intercept clearChoices and inject a real default choice
  const dispatchAction = async (action) => {
    if (action?.type === 'clearChoices') {
      // Actually add a default editable choice after clearing
      await submitAction(action);
      // Add default choice
      const defaultChoice = {
        type: 'addChoice',
        text: 'Choice 1',
        color: getRandomColor(),
        weight: 1,
      };
      return submitAction(defaultChoice);
    }
    return submitAction(action);
  };

  useEffect(() => {
    const nextDrafts = {};
    for (const choice of choices) {
      nextDrafts[choice.id] = {
        text: choice.text,
        color: choice.color,
        weight: choice.weight,
      };
    }
    setChoiceDrafts(nextDrafts);
  }, [choices]);

  useEffect(() => {
    if (expandedChoiceId && !choices.some((choice) => choice.id === expandedChoiceId)) {
      setExpandedChoiceId(null);
    }
  }, [choices, expandedChoiceId]);

  useEffect(() => {
    if (!pendingAddedChoiceId) return;
    if (!choices.some((choice) => choice.id === pendingAddedChoiceId)) return;
    setExpandedChoiceId(pendingAddedChoiceId);
    setPendingAddedChoiceId(null);
  }, [choices, pendingAddedChoiceId]);

  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (spin?.startedAt && animatedSpinRef.current !== spin.startedAt) {
      animatedSpinRef.current = spin.startedAt;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      try {
        // sound && sound.play && sound.play('ui:rolldie');
      } catch (error) {}
      const startRotation = rotationRef.current;
      const endRotation = spin.rotationDegrees || 0;
      const durationMs = Math.max(spin.durationMs || 0, 1);
      const startTime = performance.now();

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / durationMs, 1);
        const easedProgress = easeOutSpin(progress);
        const prevRotation = rotationRef.current;
        const nextRotation = startRotation + ((endRotation - startRotation) * easedProgress);
        rotationRef.current = nextRotation;
        setRotation(nextRotation);

        const crossings = countBoundaryCrossings(prevRotation, nextRotation, segmentBoundariesRef.current);
        if (crossings > 0 && sound && sound.play) {
          // Cap ticks per frame so a fast initial spin can't flood the audio mixer.
          const ticksToPlay = Math.min(crossings, 8);
          for (let i = 0; i < ticksToPlay; i += 1) {
            try { sound.play('wheel:tick'); } catch (error) {}
          }
        }

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
          return;
        }

        rotationRef.current = endRotation;
        setRotation(endRotation);
        animationFrameRef.current = null;
      };

      animationFrameRef.current = requestAnimationFrame(step);
      return;
    }

    if (phase !== 'spinning') {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      const nextRotation = gameState?.currentRotation || 0;
      rotationRef.current = nextRotation;
      setRotation(nextRotation);
    }
  }, [gameState?.currentRotation, phase, spin, sound]);

  const addChoice = async () => {
    if (isBusy) return;
    const nextChoiceNumber = choices.reduce((maxNumber, choice) => {
      const match = /^Choice\s+(\d+)$/i.exec(String(choice.text || '').trim());
      if (!match) return maxNumber;
      return Math.max(maxNumber, Number(match[1]));
    }, 0) + 1;

    const result = await dispatchAction({
      type: 'addChoice',
      text: `Choice ${nextChoiceNumber}`,
      weight: 1,
    });

    if (result && typeof result === 'object' && result.choiceId) {
      setPendingAddedChoiceId(result.choiceId);
    }
  };

  const importChoices = async () => {
    if (isBusy) return;

    const existingChoices = Array.isArray(choices) ? choices : [];
    const lines = String(importText || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const slotsAvailable = importMode === 'append'
      ? Math.max(0, MAX_CHOICES - existingChoices.length)
      : MAX_CHOICES;
    const nextChoices = lines.slice(0, slotsAvailable);
    if (nextChoices.length === 0) return;

    const sequenceStart = Math.floor(Math.random() * DEFAULT_COLORS.length);
    const shouldReplace = importMode === 'replace';

    suppressDefaultChoiceRef.current = shouldReplace;

    if (shouldReplace) {
      for (const choice of existingChoices) {
        await dispatchAction({ type: 'removeChoice', choiceId: choice.id });
      }
    }

    try {
      for (let index = 0; index < nextChoices.length; index += 1) {
        const text = nextChoices[index].slice(0, 30);
        await dispatchAction({
          type: 'addChoice',
          text,
          color: getColorFromSequence(importMode === 'append' ? existingChoices.length + index : index, sequenceStart),
          weight: 1,
        });
      }
    } finally {
      suppressDefaultChoiceRef.current = false;
    }

    setImportText('');
    setIsImportPanelOpen(false);
  };

  const toggleLockIn = async () => {
    if (isBusy || !user?.socketId) return;
    await dispatchAction({ type: 'setLockedIn', lockedIn: !isCurrentPlayerLocked });
  };

  return (
    <div className="min-h-full  p-4 text-slate-900  dark:text-slate-100">
      <div className="mx-auto flex flex-col xl:flex-row gap-8 max-w-7xl">
        {/* Wheel: break out, go wide */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div
            className="relative w-full max-w-3xl aspect-square mx-auto"
            style={{ cursor: (!isBusy && !isCurrentPlayerLocked && user?.socketId) ? 'pointer' : 'default' }}
            onClick={async () => {
              if (isBusy || isCurrentPlayerLocked || !user?.socketId) return;
              await dispatchAction({ type: 'setLockedIn', lockedIn: true });
            }}
            aria-disabled={isBusy || isCurrentPlayerLocked || !user?.socketId}
            tabIndex={-1}
          >
                <div className="pointer-events-none absolute left-1/2 top-2 z-20 -translate-x-1/2">
                  <div className="relative h-[32px] w-[40px]">
                    <div
                      className="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 border-l-[20px] border-r-[20px] border-t-[32px] border-l-transparent border-r-transparent border-t-white dark:border-t-black"
                      style={{ pointerEvents: 'none' }}
                    />
                    <div
                      className="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 border-l-[18px] border-r-[18px] border-t-[30px] border-l-transparent border-r-transparent border-t-black dark:border-t-white"
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                </div>


                <svg viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
                  <defs>
                    <filter id="wheel-shadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(15,23,42,0.35)" />
                    </filter>
                    {choices.map((choice, idx) => {
                      // Create a lighter version of the color for the gradient
                      const rgb = hexToRgb(choice.color);
                      let lighter = '#ffffff';
                      if (rgb) {
                        // Blend 70% original, 30% white
                        const r = Math.round(rgb.r * 0.7 + 255 * 0.3);
                        const g = Math.round(rgb.g * 0.7 + 255 * 0.3);
                        const b = Math.round(rgb.b * 0.7 + 255 * 0.3);
                        lighter = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                      }
                      return (
                        <radialGradient key={choice.id} id={`wheel-grad-${choice.id}`} cx="100%" cy="100%" r="100%">
                          <stop offset="60%" stopColor={choice.color} />
                          <stop offset="100%" stopColor={lighter} />
                        </radialGradient>
                      );
                    })}
                  </defs>
                  <g filter="url(#wheel-shadow)">
                    <circle cx="50" cy="50" r="22" fill="#0f172a" stroke="#1e293b" strokeWidth="1.5" />
                    {choices.length === 0 && (
                      <circle cx="50" cy="50" r="18" fill="#111827" stroke="#334155" strokeWidth="1" />
                    )}
                  </g>
                </svg>

                <div
                  className="h-full w-full"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: '50% 50%',
                  }}
                >
                  <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible">
                    <g>
                      {choices.map((choice, index) => {
                        const segment = segments.find((entry) => entry.id === choice.id);
                        if (!segment) return null;
                        const position = getSegmentLabelPosition(segment.midAngle, segment.sweepAngle < 25 ? 23 : 29);
                        const labelColor = getReadableTextColor(choice.color);
                        const labelOutline = labelColor === '#f8fafc' ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.6)';
                        const labelText = (choice.text || '').slice(0, 30);
                        const shouldUsePerpendicularLabel = labelText.length > 12 || choices.length > 8;
                        const labelRotation = shouldUsePerpendicularLabel
                          ? segment.midAngle + 90
                          : segment.midAngle;
                        // Split text into lines of max 12 chars, up to 3 lines
                        const lines = [];
                        let txt = labelText;
                        const maxLineLength = shouldUsePerpendicularLabel ? 18 : 12;
                        const maxLines = shouldUsePerpendicularLabel ? 2 : 3;
                        for (let i = 0; i < maxLines && txt.length > 0; i++) {
                          lines.push(txt.slice(0, maxLineLength));
                          txt = txt.slice(maxLineLength);
                        }
                        // Use gradient fill
                        const gradId = `wheel-grad-${choice.id}`;
                        return (
                          <g key={choice.id}>
                            {choices.length === 1 || segment.sweepAngle >= 359.95 ? (
                              <circle cx="50" cy="50" r="46" fill={`url(#${gradId})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
                            ) : (
                              <path d={describeArcPath(segment.startAngle, segment.endAngle)} fill={`url(#${gradId})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
                            )}
                            <g transform={`translate(${position.x} ${position.y}) rotate(${labelRotation})`}>
                              {lines.map((line, idx) => (
                                <text
                                  key={idx}
                                  x="0"
                                  y={idx * 4.2 - ((lines.length - 1) * 2.1)}
                                  fill={labelColor}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  stroke={labelOutline}
                                  strokeWidth="0.45"
                                  paintOrder="stroke"
                                  style={{ fontSize: segment.sweepAngle < 16 ? '2.7px' : segment.sweepAngle < 28 ? '3.1px' : '3.7px', fontWeight: 700, letterSpacing: shouldUsePerpendicularLabel ? '0.01em' : '0.02em' }}
                                >
                                  {line}
                                </text>
                              ))}
                            </g>
                            {choices.length > 1 && index < choices.length - 1 && (
                              <line
                                x1="50"
                                y1="50"
                                x2={polarToCartesian(50, 50, 46, segment.endAngle).x}
                                y2={polarToCartesian(50, 50, 46, segment.endAngle).y}
                                stroke="rgba(255,255,255,0.3)"
                                strokeWidth="0.5"
                              />
                            )}
                          </g>
                        );
                      })}
                    </g>
                  </svg>
                </div>

                <svg viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
                  <circle cx="50" cy="50" r="7" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.2" />
                  <circle cx="50" cy="50" r="5.5" fill={centerFillColor} style={{ transition: 'fill 90ms linear' }} />
                </svg>
              </div>
            </div>
        </div>
        {/* Right column: Wheel Choices and Selected Choice */}
        <div className="flex flex-col gap-6 w-full max-w-xl mx-auto xl:mx-0 xl:w-full xl:max-w-3xl">
          {/* Selected Choice pane */}
          {displayedChoice && (
            <div
              className="rounded-2xl border border-slate-200 dark:border-slate-800 shadow p-3 flex flex-col items-center justify-center gap-2"
              style={{
                background: phase === 'spinning'
                  ? 'rgba(255,255,255,0.88)'
                  : `radial-gradient(circle at 10% 40%, ${displayedChoice.color} 20%, ${(() => {
                      const rgb = hexToRgb(displayedChoice.color);
                      if (!rgb) return '#fff';
                      const r = Math.round(rgb.r * 0.7 + 255 * 0.3);
                      const g = Math.round(rgb.g * 0.7 + 255 * 0.3);
                      const b = Math.round(rgb.b * 0.7 + 255 * 0.3);
                      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    })()} 100%)`,
                color: phase === 'spinning' ? '#0f172a' : getReadableTextColor(displayedChoice.color),
                minHeight: '64px',
                transition: 'background 180ms linear, color 180ms linear',
              }}
            >
              <div style={{ minHeight: 16, width: '100%' }}>
                {phase === 'spinning' ? (
                  <div className="text-xs uppercase tracking-[0.25em] opacity-80 text-center text-slate-500">Spinning...</div>
                ) : (
                  <div className="text-xs uppercase tracking-[0.25em] opacity-80 text-center" style={{color: getReadableTextColor(displayedChoice.color)}}>Selected Choice</div>
                )}
              </div>
              <div className="text-2xl font-extrabold text-center" style={{color: phase === 'spinning' ? '#0f172a' : getReadableTextColor(displayedChoice.color)}}>{displayedChoice.text}</div>
            </div>
          )}
          {/* Status container below Selected Choice */}
          <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 mt-2">
            <div className="mt-1 flex justify-center flex-wrap gap-2">
              <button
                type="button"
                onClick={toggleLockIn}
                disabled={isBusy || !user?.socketId}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${isCurrentPlayerLocked ? 'bg-yellow-300 text-slate-900 hover:bg-yellow-400' : 'bg-emerald-500 text-white hover:bg-emerald-400'} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {players.length === 1
                  ? 'Spin the wheel'
                  : (isCurrentPlayerLocked ? 'Unlock' : 'Lock In')}
              </button>
            </div>
            {/* Only show lock-in warning if more than one player */}
            {players.length > 1 && !everyoneLocked && choices.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 dark:text-amber-200">Everyone must lock in before the wheel spins automatically.</div>
            )}

            {players.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
                {players.map((player) => (
                  <span
                    key={player.socketId}
                    className={`rounded-full px-3 py-1 font-semibold ${lockedInPlayers[player.socketId] ? 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300'}`}
                  >
                    {player.username || player.socketId}: {lockedInPlayers[player.socketId] ? 'Locked' : 'Waiting'}
                  </span>
                ))}
              </div>
            )}
            

            {/* Selected Choice now in its own pane above */}
            {!displayedChoice && (
              <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">No spin result yet.</div>
            )}
          </div>
          {/* Wheel Choices below status */}
          <ChoiceList
            choices={choices}
            drafts={choiceDrafts}
            setDrafts={setChoiceDrafts}
            isHost={isHost}
            isBusy={isBusy}
            dispatchAction={dispatchAction}
            expandedChoiceId={expandedChoiceId}
            setExpandedChoiceId={setExpandedChoiceId}
            canClear={!isBusy && choices.length > 0}
            onAddChoice={addChoice}
            onToggleImportPanel={() => setIsImportPanelOpen((current) => !current)}
            isImportPanelOpen={isImportPanelOpen}
            importText={importText}
            setImportText={setImportText}
            importMode={importMode}
            setImportMode={setImportMode}
            onImportChoices={importChoices}
            isCollapsed={isChoicesCollapsed}
            onToggleCollapsed={() => setIsChoicesCollapsed((current) => !current)}
          />
        {/* Totals bar chart below choices/selected */}
        <div className="mt-1 w-full max-w-xl mx-auto xl:mx-0 xl:w-full xl:max-w-3xl">
          <div className="rounded-[2rem] border border-white/60 bg-white/75 p-5 shadow-[0_30px_80px_rgba(15,23,42,0.14)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-[0_30px_80px_rgba(2,6,23,0.45)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsTotalsCollapsed((current) => !current)}
                className="flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-[0.35em] text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                aria-expanded={!isTotalsCollapsed}
              >
                <span>Totals</span>
                <span className="text-base leading-none">{isTotalsCollapsed ? '+' : '-'}</span>
              </button>
              {isHost && (
                <button
                  type="button"
                  onClick={() => dispatchAction({ type: 'resetSelectionTotals' })}
                  disabled={isBusy || orderedSelectionTotals.every((entry) => (entry.count || 0) === 0)}
                  className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  Reset
                </button>
              )}
            </div>
            {!isTotalsCollapsed && (
            <div className="space-y-3">
              {orderedSelectionTotals.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Add choices to start tracking totals.
                </div>
              )}
              {orderedSelectionTotals.map((entry) => {
                const widthPercent = maxSelectionCount > 0 && entry.count > 0
                  ? Math.max((entry.count / maxSelectionCount) * 100, 8)
                  : 0;
                return (
                  <div key={entry.choiceId} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-5 w-5 rounded-full border-2 shadow-sm"
                          style={{
                            backgroundColor: entry.color,
                            boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                            borderColor: (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
                              ? '#fff' // white border in dark mode
                              : '#000',   // black border in light mode
                          }}
                        />
                        <span className="truncate font-semibold text-slate-900 dark:text-slate-100">{entry.text}</span>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{entry.count}</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{ width: `${widthPercent}%`, backgroundColor: entry.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

WheelOfDestinyGame.Guide = function WheelOfDestinyGuide() {
  return (
    <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
      <p>Anyone can add a wheel choice, its color, and its weight.</p>
      <p>Anyone can recolor a choice. The host can also edit labels, weights, and removals.</p>
      <p>Everyone must lock in before the wheel spins automatically, and you can keep spinning as long as you want.</p>
      <p>When the pointer lands on a slice, that selected choice is the result, and the totals chart keeps track of how often each choice is picked.</p>
    </div>
  );
};